/*
 * The Staff & Roles screen's state, shared with the classic panel.
 *
 * What this destination actually manages is **tokens, not people**. There is no member record, no
 * invitation and no account: access is a string you hand to someone, and every question worth
 * asking on this screen is a question about that string — is it still good, when does it lapse, has
 * anyone ever used it, and what does it open.
 *
 * `roleAccess` answers the last one from `destinations.ts`, which is the registry the console
 * already navigates by. A role matrix written by hand beside it would be a second source that
 * drifts; derived, it cannot say a role opens a screen the console would not open.
 */
import { useCallback, useEffect, useState } from 'react';
import { adminApi, ApiError } from '../../../api';
import type { CheckinLogDto, CheckinTokenDto } from '../../../api';
import { EVENT_ADMIN_DESTINATIONS } from '../../destinations';
import type { ConsoleRole, Destination } from '../../destinations';

const DAY = 86_400_000;
/** How close to lapsing counts as "soon". A week is one working cycle to notice and reissue. */
export const EXPIRING_SOON_DAYS = 7;

export type TokenRole = CheckinTokenDto['role'];
export type TokenState = 'active' | 'revoked' | 'expired' | 'used_up';

/**
 * One state per token, in the order that decides which reason is reported.
 *
 * Revoked first: a token that was revoked *and* has since lapsed was stopped by a person, and that
 * is the fact worth showing. Reading it as merely expired would hide a deliberate act.
 */
export function checkinTokenState(t: CheckinTokenDto): TokenState {
  if (t.revoked) return 'revoked';
  if (t.usedUp) return 'used_up';
  if (t.expired) return 'expired';
  return 'active';
}

/** Whether a still-good token lapses within `days`. A token with no expiry never does. */
export function expiresWithin(t: CheckinTokenDto, days = EXPIRING_SOON_DAYS, now = Date.now()): boolean {
  if (checkinTokenState(t) !== 'active' || t.expiresAt === null) return false;
  const at = new Date(t.expiresAt).getTime();
  return at > now && at - now <= days * DAY;
}

export interface TokenSummary {
  total: number;
  active: number;
  revoked: number;
  expiringSoon: number;
  /** Issued and still good, but nobody has ever used it. Either it never arrived, or it is loose. */
  neverUsed: number;
  byRole: Record<TokenRole, number>;
  /** The nearest lapse among the active tokens, for the tile to name a date rather than a count. */
  nextExpiry: string | null;
}

export function summarize(tokens: readonly CheckinTokenDto[], now = Date.now()): TokenSummary {
  const byRole: Record<TokenRole, number> = { checkin: 0, sales: 0, pos: 0 };
  let active = 0;
  let revoked = 0;
  let expiringSoon = 0;
  let neverUsed = 0;
  let nextExpiry: string | null = null;

  for (const t of tokens) {
    const state = checkinTokenState(t);
    if (state === 'revoked') revoked += 1;
    if (state !== 'active') continue;
    active += 1;
    byRole[t.role] += 1;
    if (t.lastUsedAt === null) neverUsed += 1;
    if (expiresWithin(t, EXPIRING_SOON_DAYS, now)) expiringSoon += 1;
    if (t.expiresAt !== null && (nextExpiry === null || t.expiresAt < nextExpiry)) nextExpiry = t.expiresAt;
  }

  return { total: tokens.length, active, revoked, expiringSoon, neverUsed, byRole, nextExpiry };
}

export interface RoleAccess {
  role: TokenRole;
  label: string;
  /** The console role this token authenticates as, or null when it opens no console screen at all. */
  consoleRole: ConsoleRole | null;
  /** Where the holder actually works. Not every token is an admin-console token. */
  surface: string;
  path: (eventSlug?: string) => string;
  destinations: readonly Destination[];
}

const ROLE_LABEL: Record<TokenRole, string> = {
  checkin: 'เช็กอินหน้างาน', pos: 'จุดจำหน่าย (ขายบัตรหน้างาน)', sales: 'ฝ่ายขาย',
};

/**
 * What a token of this role opens, read off the registry the console navigates by.
 *
 * `pos` is the one with no console role, and that is not an omission: selling stock at the door is
 * not admitting people and not reading the back office, so a POS token authenticates to its own
 * surface and `canAccess` refuses it every admin destination. Saying that plainly is the point of
 * the panel — the frame's "Full access ทำอะไรได้บ้าง" is exactly this question.
 */
export function roleAccess(role: TokenRole): RoleAccess {
  const consoleRole: ConsoleRole | null = role === 'pos' ? null : role;
  return {
    role,
    label: ROLE_LABEL[role],
    consoleRole,
    surface: role === 'pos' ? 'หน้าจุดจำหน่าย' : role === 'checkin' ? 'หน้าเช็กอิน และคอนโซลบางหน้า' : 'คอนโซลผู้ดูแล (เฉพาะบางหน้า)',
    path: (slug?: string) => {
      const prefix = slug ? `/e/${slug}` : '';
      return role === 'pos' ? `${prefix}/staff/pos` : role === 'checkin' ? `${prefix}/staff/checkin` : `${prefix}/admin`;
    },
    destinations: consoleRole === null
      ? []
      : EVENT_ADMIN_DESTINATIONS.filter((d) => d.roles.includes(consoleRole)),
  };
}

export const TOKEN_ROLES: readonly TokenRole[] = ['checkin', 'pos', 'sales'];

export type ExpiryChoice = '24h' | '7d' | '30d' | 'never';

export interface NewTokenDraft {
  holderLabel: string;
  role: TokenRole;
  expiry: ExpiryChoice;
}

export interface NewlyIssuedToken {
  id: string;
  holderLabel: string;
  role: TokenRole;
  token: string;
}

export interface StaffTokensState {
  tokens: CheckinTokenDto[];
  log: CheckinLogDto | null;
  summary: TokenSummary;
  loading: boolean;
  loadError: string;
  logError: string;
  createError: string;
  busy: boolean;
  /**
   * TICK-177 — the one-time handoff after a create. The create response is the only place the full
   * value ever reaches the browser (list rows carry `token: null`, TICK-154), so it is held here, in
   * memory only — never in storage, the URL or a log — until the owner dismisses it. After that it
   * is gone for good; a lost value means revoke and issue again.
   */
  justCreated: NewlyIssuedToken | null;
  dismissCreated: () => void;
  create: (draft: NewTokenDraft) => Promise<boolean>;
  revoke: (t: CheckinTokenDto) => Promise<void>;
  reload: () => Promise<void>;
}

export function useStaffTokens(token: string, eventSlug: string | undefined): StaffTokensState {
  const [tokens, setTokens] = useState<CheckinTokenDto[] | null>(null);
  const [log, setLog] = useState<CheckinLogDto | null>(null);
  const [loadError, setLoadError] = useState('');
  const [logError, setLogError] = useState('');
  const [createError, setCreateError] = useState('');
  const [busy, setBusy] = useState(false);
  const [justCreated, setJustCreated] = useState<NewlyIssuedToken | null>(null);

  const load = useCallback(async () => {
    try {
      setTokens(await adminApi.listCheckinTokens(token, eventSlug));
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดรายการ Token ไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  const loadLog = useCallback(async () => {
    try {
      setLog(await adminApi.getCheckinLog(token, eventSlug));
      setLogError('');
    } catch {
      setLogError('โหลดบันทึกการเช็กอินไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { void load(); void loadLog(); }, [load, loadLog]);

  const create = useCallback(async (draft: NewTokenDraft) => {
    const label = draft.holderLabel.trim();
    if (!label) return false;
    setBusy(true);
    setCreateError('');
    try {
      const created = await adminApi.createCheckinToken(token, label, draft.expiry, eventSlug, draft.role);
      setJustCreated({ id: created.id, holderLabel: created.holderLabel, role: created.role, token: created.token });
      await load();
      return true;
    } catch (e) {
      setCreateError(e instanceof ApiError ? e.message : 'สร้าง Token ไม่สำเร็จ');
      return false;
    } finally {
      setBusy(false);
    }
  }, [token, eventSlug, load]);

  const revoke = useCallback(async (t: CheckinTokenDto) => {
    setBusy(true);
    try {
      await adminApi.revokeCheckinToken(token, t.id, eventSlug);
      // A revoked value is worthless to hand out; stop offering it.
      setJustCreated((current) => (current?.id === t.id ? null : current));
      await load();
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'เพิกถอน Token ไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }, [token, eventSlug, load]);

  return {
    tokens: tokens ?? [],
    log,
    summary: summarize(tokens ?? []),
    loading: tokens === null && !loadError,
    loadError,
    logError,
    createError,
    busy,
    justCreated,
    dismissCreated: () => setJustCreated(null),
    create,
    revoke,
    reload: load,
  };
}
